import { create } from 'zustand';
import ServiceCertidaoFederal, {
  TIPO_LABEL,
  ORIENTACAO_LABEL,
} from '@/services/ServiceCertidaoFederal';
import { notify } from '@/stores/useToast';
import { mensagemErro } from '@/utils/apiError';
import UtilsDate from '@/utils/UtilsDate';

type Any = any;

const fmtData = (v?: string) => (v ? new Date(v).toLocaleDateString('pt-BR') : '');
// valores crus p/ o prefill de edição (o DTO do BFF espera ISO YYYY-MM-DD + tipo enum)
const isoDate = (v?: string) => (v ? String(v).slice(0, 10) : '');
const isoHora = (v?: string) => {
  const m = v && String(v).match(/T(\d{2}:\d{2})/);
  return m ? m[1] : '';
};

interface Dashboard {
  quantidadeTotalMonitoradas: number;
  percentualTotalMonitoradas: number;
  quantidadeSucesso: number;
  percentualSucesso: number;
  quantidadeIndisponivel: number;
  percentualIndisponivel: number;
}

interface Filtro {
  empregador: string;
  tipo: string;
}
const FILTRO_VAZIO: Filtro = { empregador: '', tipo: '' };

// CON-5188: cada coluna ordenável da tabela mapeia para o alias que o backend aceita
// (certidao-federal/search com sort=<alias>,<asc|desc>). Só as colunas com alias entram
// como sortable — as demais não têm suporte de ordenação no BFF.
const SORT_ALIASES: Record<string, string> = {
  empregador: 'empregador',
  cnpj: 'cnpj',
  tipo: 'tipo',
  emissao: 'emissao',
  validade: 'validade',
  codigo: 'codigo',
};

interface State {
  lista: Any[];
  total: number;
  page: number;
  size: number;
  busca: string;
  filtro: Filtro;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  dashboard: Dashboard | null;
  carregando: boolean;
  sincronizando: boolean;
  exportando: boolean;
  historico: Any[];
  carregar: () => Promise<void>;
  carregarDashboard: () => Promise<void>;
  setBusca: (v: string) => void;
  buscar: () => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setSize: (size: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  aplicarFiltro: (f: Filtro) => Promise<void>;
  limparFiltro: () => Promise<void>;
  sincronizar: (cnpj: string) => Promise<void>;
  salvar: (dto: Any) => Promise<void>;
  atualizar: (id: string, dto: Any) => Promise<void>;
  excluir: (id: string) => Promise<void>;
  carregarHistorico: (id: string) => Promise<void>;
  exportar: () => Promise<void>;
}

// item da API -> linha da tabela. Mantém `cnpj` CRU (o modal de edição casa o select
// pelo cnpjFilial cru) — a máscara é aplicada só no render da coluna.
const normalizar = (c: Any) => ({
  id: c.id,
  empregador: c.nomeFantasia ?? '',
  cnpj: c.cnpj ?? '',
  tipo: TIPO_LABEL[c.tipo as keyof typeof TIPO_LABEL] ?? c.tipo ?? '',
  emissao: fmtData(c.dataEmissao),
  validade: fmtData(c.dataValidade),
  codigo: c.codigoControle ?? '',
  orientacao: ORIENTACAO_LABEL[c.codigoRetorno as keyof typeof ORIENTACAO_LABEL] ?? '',
  arquivoCndId: c.arquivoCndId ?? '',
  arquivoPendenciaId: c.arquivoPendenciaId ?? '',
  // crus p/ edição (não exibidos na tabela)
  _tipo: c.tipo ?? 'CND',
  _emissao: isoDate(c.dataEmissao),
  _validade: isoDate(c.dataValidade),
  _hora: isoHora(c.dataEmissao),
});

// corpo do /search a partir do estado. O BFF filtra por `empregador` (ilike em
// nome_fantasia OU cnpj OU codigo_controle) e `tipo`. Precedência: empregador do filtro > busca.
const _body = (get: () => State) => {
  const { page, size, busca, filtro, sortBy, sortOrder } = get();
  const empregador = filtro.empregador || busca || undefined;
  const tipo = filtro.tipo || undefined;
  const filtroDTO = empregador || tipo ? { empregador, tipo } : undefined;
  const alias = SORT_ALIASES[sortBy];
  const sort = alias ? `${alias},${sortOrder}` : undefined;
  return { filtro: filtroDTO, page, size, ...(sort ? { sort } : {}) };
};

export const useCertidaoFederalStore = create<State>((set, get) => ({
  lista: [],
  total: 0,
  page: 0,
  size: 50,
  busca: '',
  filtro: { ...FILTRO_VAZIO },
  sortBy: '',
  sortOrder: 'asc',
  dashboard: null,
  carregando: false,
  sincronizando: false,
  exportando: false,
  historico: [],

  async carregar() {
    set({ carregando: true });
    try {
      const { data } = await ServiceCertidaoFederal.search(_body(get));
      set({ lista: (data?.items ?? []).map(normalizar), total: data?.total ?? 0 });
    } catch {
      notify.error('Erro ao carregar certidões.');
      set({ lista: [], total: 0 });
    } finally {
      set({ carregando: false });
    }
  },

  async carregarDashboard() {
    try {
      const { data } = await ServiceCertidaoFederal.dashboard();
      set({ dashboard: data ?? null });
    } catch {
      set({ dashboard: null });
    }
  },

  setBusca(v) {
    set({ busca: v });
  },

  async buscar() {
    set({ page: 0 });
    await get().carregar();
  },

  async setPage(page) {
    set({ page });
    await get().carregar();
  },

  async setSize(size) {
    set({ size, page: 0 });
    await get().carregar();
  },

  async setSort(by, order) {
    set({ sortBy: by, sortOrder: order, page: 0 });
    await get().carregar();
  },

  async aplicarFiltro(f) {
    set({ filtro: f, page: 0 });
    await get().carregar();
  },

  async limparFiltro() {
    set({ filtro: { ...FILTRO_VAZIO }, page: 0 });
    await get().carregar();
  },

  async sincronizar(cnpj) {
    if (!cnpj) {
      notify.error('Selecione um empregador.');
      return;
    }
    set({ sincronizando: true });
    try {
      await ServiceCertidaoFederal.sincronizar(cnpj);
      await get().carregar();
      await get().carregarDashboard();
      notify.success('Certidão sincronizada.');
    } catch (err) {
      notify.error(mensagemErro(err, 'Erro ao sincronizar a certidão.'));
    } finally {
      set({ sincronizando: false });
    }
  },

  async salvar(dto) {
    try {
      await ServiceCertidaoFederal.salvar(dto);
      await get().carregar();
      await get().carregarDashboard();
      notify.success('Certidão salva.');
    } catch {
      notify.error('Erro ao salvar a certidão.');
    }
  },

  async atualizar(id, dto) {
    try {
      await ServiceCertidaoFederal.atualizar(id, dto);
      await get().carregar();
      await get().carregarDashboard();
      notify.success('Certidão atualizada.');
    } catch {
      notify.error('Erro ao atualizar a certidão.');
    }
  },

  async excluir(id) {
    try {
      await ServiceCertidaoFederal.excluir(id);
      await get().carregar();
      await get().carregarDashboard();
      notify.success('Certidão excluída.');
    } catch {
      notify.error('Erro ao excluir a certidão.');
    }
  },

  async carregarHistorico(id) {
    try {
      const { data } = await ServiceCertidaoFederal.historico(id, { page: 0, size: 50 });
      // CON-5300: histórico com data em DD/MM/AAAA (emissão com hora -> DD/MM/AAAA HH:mm:ss).
      const historico = (data?.items ?? []).map((h: Any) => ({
        ...h,
        dataEmissao: UtilsDate.formatBR(h.dataEmissao, { comHora: true }),
        dataValidade: UtilsDate.formatBR(h.dataValidade),
      }));
      set({ historico });
    } catch {
      set({ historico: [] });
    }
  },

  async exportar() {
    set({ exportando: true });
    try {
      await ServiceCertidaoFederal.exportData(_body(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Não foi possível exportar as certidões.');
    } finally {
      set({ exportando: false });
    }
  },
}));
